import { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { PageRefreshContext } from '../../contexts/PageRefreshContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { permissionMap } from './permissionMap';
import { useTranslation } from 'react-i18next';
import TitleBar from './TitleBar';
import Sidebar from './Sidebar';
import TopActionBar from './TopActionBar';
import StudentListing from '../students/StudentListing';
import CreateOrganization from '../organization/CreateOrganization';
import AcademicSessions from '../academic-sessions/AcademicSessions';
import Departments from '../departments/Departments';
import Designations from '../designations/Designations';
import SchoolManagement from '../schools/SchoolManagement';
import ClassManagement from '../classes/ClassManagement';
import SubjectMaster from '../subjects/SubjectMaster';
import SchoolSubjectAllocation from '../subjects/SchoolSubjectAllocation';
import SubjectAllocation from '../allocations/SubjectAllocation';
import TimetableBuilder from '../timetable/TimetableBuilder';
import StaffManagement from '../staff/StaffManagement';
import ExamMaster from '../exams/ExamMaster';
import MarksEntry from '../marks/MarksEntry';
import ViewResults from '../results/ViewResults';
import FeeStructures from '../fees/FeeStructures';
import StudentDiscounts from '../fees/StudentDiscounts';
import StudentInvoices from '../invoices/StudentInvoices';
import CollectionDesk from '../collection/CollectionDesk';
import FleetManagement from '../transport/FleetManagement';
import RouteBuilder from '../routes/RouteBuilder';
import PassengerAllocations from '../transport-allocations/PassengerAllocations';
import Attendance from '../attendance/Attendance';
import ExpenseCategories from '../expenses/ExpenseCategories';
import ExpenseLedger from '../expenses/ExpenseLedger';
import TransactionLedger from '../transactions/TransactionLedger';
import FeeDefaulters from '../fee-defaulters/FeeDefaulters';
import GatePass from '../gatepass/GatePass';
import ReportCenter from '../reports/ReportCenter';
import HomeworkLedger from '../homework/HomeworkLedger';
import NoticeLedger from '../noticeboard/NoticeLedger';
import HolidayLedger from '../holidays/HolidayLedger';
import TaskManagement from '../tasks/TaskManagement';
import HomeworkEvaluation from '../homework/HomeworkEvaluation';
import { pathToIdMap } from './Sidebar';
import RupeeIcon from '../icons/RupeeIcon';
import { apiRequest } from '../../services/api';
import { getSchoolId } from '../../services/auth';
import './DashboardLayout.css';

/** Maps sidebar id → i18n key in pageTitles namespace */
const pageTitleKeys: Record<string, string> = {
    dashboard: 'pageTitles.dashboard',
    students: 'pageTitles.students',
    staff: 'pageTitles.staff',
    schools: 'pageTitles.schools',
    classes: 'pageTitles.classes',
    subjects: 'pageTitles.subjects',
    allocations: 'pageTitles.allocations',
    attendance: 'pageTitles.attendance',
    timetable: 'pageTitles.timetable',
    gatePass: 'pageTitles.gatePass',
    exams: 'pageTitles.exams',
    marks: 'pageTitles.marks',
    results: 'pageTitles.results',
    feeStructures: 'pageTitles.feeStructures',
    invoices: 'pageTitles.invoices',
    collection: 'pageTitles.collection',
    transactions: 'pageTitles.transactions',
    feeDefaulters: 'pageTitles.feeDefaulters',
    expenses: 'pageTitles.expenses',
    expenseCategories: 'pageTitles.expenseCategories',
    fleet: 'pageTitles.fleet',
    routeBuilder: 'pageTitles.routeBuilder',
    passengerAlloc: 'pageTitles.passengerAlloc',
    reports: 'pageTitles.reports',
    homework: 'pageTitles.homework',
    homeworkEval: 'pageTitles.homeworkEval',
    noticeboard: 'pageTitles.noticeboard',
    holidays: 'pageTitles.holidays',
    organization: 'pageTitles.organization',
    departments: 'pageTitles.departments',
    designations: 'pageTitles.designations',
    sessions: 'pageTitles.sessions',
    schoolSubjects: 'pageTitles.schoolSubjects',
    tasks: 'pageTitles.tasks',
};

/* ── Guards a route by sidebar item id ── */
function PermissionGate({ sidebarId, children }: { sidebarId: string; children: React.ReactNode }) {
    const { hasAnyPermission } = usePermissions();
    const { t } = useTranslation();
    const perms = permissionMap[sidebarId] ?? [];
    if (perms.length > 0 && !hasAnyPermission(perms)) {
        return (
            <div className="permission-denied">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                </svg>
                <h2>{t('errors.accessDenied', 'Access Denied')}</h2>
                <p>{t('errors.noPermission', 'You do not have permission to view this page.')}</p>
            </div>
        );
    }
    return <>{children}</>;
}

/* ── Dashboard welcome content (extracted to keep Routes clean) ── */
function DashboardHome() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [studentCount, setStudentCount] = useState<number | null>(null);
    const [studentCountLoading, setStudentCountLoading] = useState(true);
    const [teacherCount, setTeacherCount] = useState<number | null>(null);
    const [teacherCountLoading, setTeacherCountLoading] = useState(true);
    const [attendancePct, setAttendancePct] = useState<number | null>(null);
    const [attendancePctLoading, setAttendancePctLoading] = useState(true);
    const [feeCollection, setFeeCollection] = useState<number | null>(null);
    const [feeCollectionLoading, setFeeCollectionLoading] = useState(true);

    useEffect(() => {
        const schoolId = getSchoolId();
        if (!schoolId) { setStudentCountLoading(false); setTeacherCountLoading(false); setAttendancePctLoading(false); setFeeCollectionLoading(false); return; }
        (async () => {
            try {
                const res = await apiRequest<{ student_count: number }>(`/api/schools/${schoolId}/student-count`);
                setStudentCount(res.data.student_count);
            } catch {
                setStudentCount(null);
            } finally {
                setStudentCountLoading(false);
            }
        })();
        (async () => {
            try {
                const res = await apiRequest<{ teacher_count: number }>(`/api/schools/${schoolId}/dashboard/teacher-count`);
                setTeacherCount(res.data.teacher_count);
            } catch {
                setTeacherCount(null);
            } finally {
                setTeacherCountLoading(false);
            }
        })();
        (async () => {
            try {
                const res = await apiRequest<{ attendance_percentage: number }>(`/api/schools/${schoolId}/dashboard/attendance-percentage`);
                setAttendancePct(res.data.attendance_percentage);
            } catch {
                setAttendancePct(null);
            } finally {
                setAttendancePctLoading(false);
            }
        })();
        (async () => {
            try {
                const res = await apiRequest<{ fee_collection: number }>(`/api/schools/${schoolId}/dashboard/fee-collection`);
                setFeeCollection(res.data.fee_collection ?? null);
            } catch {
                setFeeCollection(null);
            } finally {
                setFeeCollectionLoading(false);
            }
        })();
    }, []);

    return (
        <div className="welcome-section">
            <h1 className="welcome-section__title">
                {t('dashboard.greeting')}
            </h1>
            <p className="welcome-section__subtitle">
                {t('dashboard.subtitle')}
            </p>
            <div className="stats-grid">
                <div className="stat-card">
                    <div className="stat-card__icon stat-card__icon--purple">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                            <circle cx="9" cy="7" r="4" />
                            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                        </svg>
                    </div>
                    <div className="stat-card__info">
                        <span className="stat-card__value">
                            {studentCountLoading ? (
                                <svg className="stat-card__spinner" width="18" height="18" viewBox="0 0 24 24" fill="none">
                                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeDasharray="50" strokeDashoffset="35" />
                                </svg>
                            ) : (
                                studentCount !== null ? studentCount.toLocaleString() : '—'
                            )}
                        </span>
                        <span className="stat-card__label">{t('dashboard.totalStudents')}</span>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-card__icon stat-card__icon--cyan">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                            <circle cx="12" cy="7" r="4" />
                        </svg>
                    </div>
                    <div className="stat-card__info">
                        <span className="stat-card__value">
                            {teacherCountLoading ? (
                                <svg className="stat-card__spinner" width="18" height="18" viewBox="0 0 24 24" fill="none">
                                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeDasharray="50" strokeDashoffset="35" />
                                </svg>
                            ) : (
                                teacherCount !== null ? teacherCount.toLocaleString() : '—'
                            )}
                        </span>
                        <span className="stat-card__label">{t('dashboard.teachers')}</span>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-card__icon stat-card__icon--green">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                            <line x1="16" y1="2" x2="16" y2="6" />
                            <line x1="8" y1="2" x2="8" y2="6" />
                            <line x1="3" y1="10" x2="21" y2="10" />
                            <path d="m9 16 2 2 4-4" />
                        </svg>
                    </div>
                    <div className="stat-card__info">
                        <span className="stat-card__value">
                            {attendancePctLoading ? (
                                <svg className="stat-card__spinner" width="18" height="18" viewBox="0 0 24 24" fill="none">
                                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeDasharray="50" strokeDashoffset="35" />
                                </svg>
                            ) : (
                                attendancePct !== null ? `${attendancePct}%` : '—'
                            )}
                        </span>
                        <span className="stat-card__label">{t('dashboard.attendanceToday')}</span>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-card__icon stat-card__icon--amber">
                        <RupeeIcon size={22} />
                    </div>
                    <div className="stat-card__info">
                        <span className="stat-card__value">
                            {feeCollectionLoading ? (
                                <svg className="stat-card__spinner" width="18" height="18" viewBox="0 0 24 24" fill="none">
                                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeDasharray="50" strokeDashoffset="35" />
                                </svg>
                            ) : (
                                feeCollection != null ? `₹${feeCollection.toLocaleString('en-IN')}` : '—'
                            )}
                        </span>
                        <span className="stat-card__label">{t('dashboard.feesCollected')}</span>
                    </div>
                </div>
            </div>

            {/* ── Quick Actions ── */}
            <h2 className="quick-actions__heading">{t('dashboard.quickActions')}</h2>
            <div className="quick-actions">
                <button className="quick-action" onClick={() => navigate('/collection')}>
                    <div className="quick-action__icon quick-action__icon--emerald">
                        <RupeeIcon size={24} />
                    </div>
                    <div className="quick-action__body">
                        <span className="quick-action__title">{t('dashboard.collectFee')}</span>
                        <span className="quick-action__desc">{t('dashboard.collectFeeDesc')}</span>
                    </div>
                    <svg className="quick-action__arrow" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                </button>

                <button className="quick-action" onClick={() => navigate('/noticeboard')}>
                    <div className="quick-action__icon quick-action__icon--violet">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                        </svg>
                    </div>
                    <div className="quick-action__body">
                        <span className="quick-action__title">{t('dashboard.publishNotice')}</span>
                        <span className="quick-action__desc">{t('dashboard.publishNoticeDesc')}</span>
                    </div>
                    <svg className="quick-action__arrow" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                </button>

                <button className="quick-action" onClick={() => navigate('/timetable')}>
                    <div className="quick-action__icon quick-action__icon--sky">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10" />
                            <polyline points="12 6 12 12 16 14" />
                        </svg>
                    </div>
                    <div className="quick-action__body">
                        <span className="quick-action__title">{t('dashboard.viewTimetable')}</span>
                        <span className="quick-action__desc">{t('dashboard.viewTimetableDesc')}</span>
                    </div>
                    <svg className="quick-action__arrow" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                </button>

                <button className="quick-action" onClick={() => navigate('/attendance')}>
                    <div className="quick-action__icon quick-action__icon--rose">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                            <line x1="16" y1="2" x2="16" y2="6" />
                            <line x1="8" y1="2" x2="8" y2="6" />
                            <line x1="3" y1="10" x2="21" y2="10" />
                            <path d="m9 16 2 2 4-4" />
                        </svg>
                    </div>
                    <div className="quick-action__body">
                        <span className="quick-action__title">{t('dashboard.viewAttendance')}</span>
                        <span className="quick-action__desc">{t('dashboard.viewAttendanceDesc')}</span>
                    </div>
                    <svg className="quick-action__arrow" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                </button>
            </div>
        </div>
    );
}

/* ── Wrapper that passes navigate to HomeworkLedger ── */
function HomeworkPage() {
    const navigate = useNavigate();
    return (
        <HomeworkLedger
            onEvaluate={(hw) => {
                navigate('/homework/evaluate', { state: { homework: hw } });
            }}
        />
    );
}

/* ── Wrapper that reads location state for HomeworkEvaluation ── */
function HomeworkEvalPage() {
    const location = useLocation();
    const navigate = useNavigate();
    const homework = (location.state as { homework?: unknown })?.homework ?? null;
    return (
        <HomeworkEvaluation
            homework={homework as import('../homework/homeworkTypes').HomeworkItem | null}
            onBack={() => navigate('/homework')}
        />
    );
}

interface DashboardLayoutProps {
    onLogout?: () => void;
}

export default function DashboardLayout({ onLogout }: DashboardLayoutProps) {
    const { t } = useTranslation();
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const location = useLocation();

    // Derive active sidebar id from the current path for breadcrumbs / title
    const firstSegment = '/' + location.pathname.split('/').filter(Boolean)[0];
    const activeId = pathToIdMap[firstSegment] || 'dashboard';
    const titleKey = pageTitleKeys[activeId] || 'pageTitles.dashboard';
    const breadcrumbs = ['Gurukul', t(titleKey)];

    return (
        <PageRefreshContext.Provider value={() => setRefreshKey((k) => k + 1)}>
            <div className="dashboard-layout" style={{ '--sidebar-w': sidebarCollapsed ? '56px' : '220px' } as React.CSSProperties}>
                <TitleBar />

                <div className="dashboard-layout__body">
                    <Sidebar
                        collapsed={sidebarCollapsed}
                        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
                        onLogout={onLogout}
                    />

                    <div className="dashboard-layout__main">
                        <TopActionBar breadcrumbs={breadcrumbs} />

                        {/* Main Workspace — solid background, no glassmorphism */}
                        <main className="dashboard-layout__workspace">
                            <div key={refreshKey} className="dashboard-layout__content">
                                <Routes>
                                    <Route path="dashboard" element={<DashboardHome />} />
                                    <Route path="students" element={<PermissionGate sidebarId="students"><StudentListing /></PermissionGate>} />
                                    <Route path="staff" element={<PermissionGate sidebarId="staff"><StaffManagement /></PermissionGate>} />
                                    <Route path="schools" element={<PermissionGate sidebarId="schools"><SchoolManagement /></PermissionGate>} />
                                    <Route path="classes" element={<PermissionGate sidebarId="classes"><ClassManagement /></PermissionGate>} />
                                    <Route path="subjects" element={<PermissionGate sidebarId="subjects"><SubjectMaster /></PermissionGate>} />
                                    <Route path="allocations" element={<PermissionGate sidebarId="allocations"><SubjectAllocation /></PermissionGate>} />
                                    <Route path="timetable" element={<PermissionGate sidebarId="timetable"><TimetableBuilder /></PermissionGate>} />
                                    <Route path="gate-pass" element={<PermissionGate sidebarId="gatePass"><GatePass /></PermissionGate>} />
                                    <Route path="attendance" element={<PermissionGate sidebarId="attendance"><Attendance /></PermissionGate>} />
                                    <Route path="exams" element={<PermissionGate sidebarId="exams"><ExamMaster /></PermissionGate>} />
                                    <Route path="marks" element={<PermissionGate sidebarId="marks"><MarksEntry /></PermissionGate>} />
                                    <Route path="results" element={<PermissionGate sidebarId="results"><ViewResults /></PermissionGate>} />
                                    <Route path="fee-structures" element={<PermissionGate sidebarId="feeStructures"><FeeStructures /></PermissionGate>} />
                                    <Route path="student-discounts" element={<PermissionGate sidebarId="studentDiscounts"><StudentDiscounts /></PermissionGate>} />
                                    <Route path="invoices" element={<PermissionGate sidebarId="invoices"><StudentInvoices /></PermissionGate>} />
                                    <Route path="collection" element={<PermissionGate sidebarId="collection"><CollectionDesk /></PermissionGate>} />
                                    <Route path="transactions" element={<PermissionGate sidebarId="transactions"><TransactionLedger /></PermissionGate>} />
                                    <Route path="fee-defaulters" element={<PermissionGate sidebarId="feeDefaulters"><FeeDefaulters /></PermissionGate>} />
                                    <Route path="expenses" element={<PermissionGate sidebarId="expenses"><ExpenseLedger /></PermissionGate>} />
                                    <Route path="expense-categories" element={<PermissionGate sidebarId="expenseCategories"><ExpenseCategories /></PermissionGate>} />
                                    <Route path="fleet" element={<PermissionGate sidebarId="fleet"><FleetManagement /></PermissionGate>} />
                                    <Route path="route-builder" element={<PermissionGate sidebarId="routeBuilder"><RouteBuilder /></PermissionGate>} />
                                    <Route path="passenger-allocations" element={<PermissionGate sidebarId="passengerAlloc"><PassengerAllocations /></PermissionGate>} />
                                    <Route path="reports" element={<PermissionGate sidebarId="reports"><ReportCenter /></PermissionGate>} />
                                    <Route path="homework" element={<PermissionGate sidebarId="homework"><HomeworkPage /></PermissionGate>} />
                                    <Route path="homework/evaluate" element={<PermissionGate sidebarId="homework"><HomeworkEvalPage /></PermissionGate>} />
                                    <Route path="noticeboard" element={<PermissionGate sidebarId="noticeboard"><NoticeLedger /></PermissionGate>} />
                                    <Route path="holidays" element={<PermissionGate sidebarId="holidays"><HolidayLedger /></PermissionGate>} />
                                    <Route path="tasks" element={<PermissionGate sidebarId="tasks"><TaskManagement /></PermissionGate>} />
                                    <Route path="organization" element={<PermissionGate sidebarId="organization"><CreateOrganization /></PermissionGate>} />
                                    <Route path="sessions" element={<PermissionGate sidebarId="sessions"><AcademicSessions /></PermissionGate>} />
                                    <Route path="departments" element={<PermissionGate sidebarId="departments"><Departments /></PermissionGate>} />
                                    <Route path="designations" element={<PermissionGate sidebarId="designations"><Designations /></PermissionGate>} />
                                    <Route path="school-subjects" element={<PermissionGate sidebarId="schoolSubjects"><SchoolSubjectAllocation /></PermissionGate>} />
                                    {/* Catch-all: redirect unknown paths to dashboard */}
                                    <Route path="*" element={<Navigate to="/dashboard" replace />} />
                                </Routes>
                            </div>
                        </main>
                    </div>
                </div>
            </div>
        </PageRefreshContext.Provider>
    );
}
